import { Fragment, type ReactNode, type FormEvent, useState, type ReactElement } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import cn from 'classnames'
import { useSnapshot } from 'valtio'
import { proxyGlobalAuth } from '../../proxies/proxyGlobalAuth'
import XMarkIcon from './icons/XMarkIcon'
import Spinner from './Spinner'

interface Props {
  isOpen: boolean
  canSubmit: boolean
  title: string
  submitText?: string
  cancelText?: string
  cancelFunction: () => void
  submitFunction?: (e: FormEvent<HTMLFormElement>) => Promise<void>
  children: ReactNode
}

export function SlideOver ({ isOpen, canSubmit, title, submitText = 'Save', cancelText = 'Cancel', cancelFunction, submitFunction, children }: Props): ReactElement {
  const { siteTheme: { buttonColors, horizontalLine } } = useSnapshot(proxyGlobalAuth)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()

    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
    }, 5000)

    try {
      // Run the submit function passed from the parent component
      if (submitFunction) {
        await submitFunction(e)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => { }}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-55 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-scroll">
          <div className="absolute inset-0 overflow-scroll">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full min-h-screen pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-xl">
                  <form className="h-full" onSubmit={handleSubmit}>
                    <div className="bg-white flex h-full flex-col  shadow-xl">
                      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll ">
                        <div className="bg-gray-300 px-4 sm:px-6 py-4">
                          <div className=" flex items-start justify-between">
                            <Dialog.Title className=" font-semibold text-xl leading-6 text-gray-900">
                              {title}
                            </Dialog.Title>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                onClick={cancelFunction}
                              >
                                <span className="sr-only">Close panel</span>
                                <XMarkIcon color="#555" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="relative mt-2 flex-1 px-4 sm:px-6">{children}</div>
                      </div>
                      <div className={cn(horizontalLine.primaryClasses, 'flex flex-shrink-0 justify-around px-4 py-4 border-t-2')}>
                        <button
                          type="button"
                          className={cn(
                            buttonColors.secondaryClasses,
                            'w-36 flex justify-center items-center py-2 px-4 rounded-md text-sm font-medium cursor-pointer focus:outline-none'
                          )}
                          onClick={cancelFunction}
                        >
                          {cancelText}
                        </button>
                        {submitFunction &&
                          <button
                            type="submit"
                            disabled={!canSubmit || isSubmitting}
                            className={cn(
                              buttonColors.classes, 'w-48',
                              'flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2'
                            )}
                          >
                            {
                          isSubmitting
                            ? (<Spinner additionalClasses="-ml-1 mr-3" />)
                            : submitText
                          }
                          </button>}
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
